import type { PageResult } from '@jiaofa/api-types'
import { request } from './request'

export interface AdminAccount {
  id: string
  username: string
  nickname?: string
  phone?: string
  roles?: string[]
  permissions?: string[]
  storeIds?: string[]
  status?: string
  createdAt?: string
}

export interface AdminSavePayload {
  id?: string
  account: string
  password?: string
  nickname?: string
  roleIds?: string[]
  storeIds?: string[]
  status?: string
}

export interface AdminRole {
  id: string
  name: string
  code?: string
  dataScope?: string
  permissions?: string[]
  status?: string
}

export interface RoleSavePayload {
  id?: string
  name: string
  code?: string
  dataScope?: string
  permissions?: string[]
}

export interface SystemPermission {
  id: string
  parentId: string
  type: 'MENU' | 'BUTTON'
  code: string
  name: string
  path?: string | null
  sort: number
}

export interface OperationLog {
  id: string
  operatorId?: string
  operatorName?: string
  module?: string
  action?: string
  ip?: string
  createdAt?: string
}

export interface SystemParam {
  id: string
  key: string
  value?: string
  groupName?: string
  remark?: string
  updatedAt?: string
}

export interface SystemParamSavePayload {
  id?: string
  key: string
  value?: string
  groupName?: string
  remark?: string
}

export interface PaymentConfig {
  id: string
  channel: string
  appId?: string
  mchId?: string
  notifyUrl?: string
  enabled?: boolean
  configStatus?: string
}

export interface PaymentConfigSavePayload {
  id?: string
  channel: string
  appId?: string
  mchId?: string
  apiKey?: string
  apiV3Key?: string
  notifyUrl?: string
  enabled?: boolean
}

export const systemApiReady = true as const

export const fetchAdmins = async (params: { page?: number; pageSize?: number }): Promise<PageResult<AdminAccount>> =>
  request<PageResult<AdminAccount>>({
    url: '/system/admins',
    method: 'GET',
    params,
  })

export const createAdmin = async (payload: AdminSavePayload): Promise<void> =>
  request<void, AdminSavePayload>({
    url: '/system/admins',
    method: 'POST',
    data: payload,
  })

export const updateAdmin = async (payload: AdminSavePayload): Promise<void> =>
  request<void, AdminSavePayload>({
    url: '/system/admins',
    method: 'PUT',
    data: payload,
  })

export const deleteAdmin = async (id: string): Promise<void> =>
  request<void>({
    url: `/system/admins/${id}`,
    method: 'DELETE',
  })

export const fetchRoles = async (): Promise<AdminRole[]> =>
  request<AdminRole[]>({
    url: '/system/roles',
    method: 'GET',
  })

export const fetchPermissions = async (): Promise<SystemPermission[]> =>
  request<SystemPermission[]>({
    url: '/system/permissions',
    method: 'GET',
  })

export const createRole = async (payload: RoleSavePayload): Promise<void> =>
  request<void, RoleSavePayload>({
    url: '/system/roles',
    method: 'POST',
    data: payload,
  })

export const updateRole = async (payload: RoleSavePayload): Promise<void> =>
  request<void, RoleSavePayload>({
    url: '/system/roles',
    method: 'PUT',
    data: payload,
  })

export const deleteRole = async (id: string): Promise<void> =>
  request<void>({
    url: `/system/roles/${id}`,
    method: 'DELETE',
  })

export const fetchOperationLogs = async (params: { page?: number; pageSize?: number }): Promise<PageResult<OperationLog>> =>
  request<PageResult<OperationLog>>({
    url: '/system/logs',
    method: 'GET',
    params,
  })

export const fetchSystemParams = async (): Promise<SystemParam[]> =>
  request<SystemParam[]>({
    url: '/system/params',
    method: 'GET',
  })

export const updateSystemParams = async (payload: SystemParamSavePayload[]): Promise<void> =>
  request<void, SystemParamSavePayload[]>({
    url: '/system/params',
    method: 'PUT',
    data: payload,
  })

export const deleteSystemParam = async (id: string): Promise<void> =>
  request<void>({
    url: `/system/params/${id}`,
    method: 'DELETE',
  })

export const fetchPaymentConfigs = async (): Promise<PaymentConfig[]> =>
  request<PaymentConfig[]>({
    url: '/payment-config',
    method: 'GET',
  })

export const updatePaymentConfig = async (payload: PaymentConfigSavePayload): Promise<void> =>
  request<void, PaymentConfigSavePayload>({
    url: '/payment-config',
    method: 'PUT',
    data: payload,
  })
